/**
 * מנוי ומכסות — what a customer bought, and the four things an operator may change about it.
 *
 * The whole reading half of the screen is four numbers said plainly: companies purchased, companies
 * used, seats purchased, seats used. 09 §5.1's rule about figures — *"313 is not 313"* — is why every
 * line carries all three of purchased, used and remaining rather than a single usage count that
 * nobody can reconcile against an invoice.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * WHAT MOVED ONTO THIS SCREEN, AND WHAT DELIBERATELY DID NOT
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ON: the plan, the SKUs, the trial and the two limits. All four are ENTITLEMENT — what the customer
 * may do and how much of it — which is exactly what this screen already displayed and could not
 * change. A capacity screen an operator cannot act on sends them to a colleague with a database.
 *
 * NOT ON: discounts. 08 §8.4 / CF-66 keep money out of entitlement entirely — *"two tenants identical
 * but for `discount_pct` must produce byte-identical decisions"* — and this screen's original header
 * named a capacity view showing a price as the first place the two get welded together again. So the
 * discount routes live on their own address, `PlatformCommercialScreen`, and the only thing that
 * crosses is a link.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * A LIMIT MAY BE SET BELOW CURRENT USAGE, AND THE SCREEN SAYS SO BEFORE THE CLICK
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `capacity.ts` rule 1: capacity refuses CREATION and never degrades what exists. Rule 2:
 * over-capacity is a real state. So lowering a limit under current usage SUCCEEDS, reports how far
 * over the customer now is, and deletes nothing — and an operator who was not told that in advance
 * would reasonably believe they had just destroyed eighteen companies.
 *
 * `null` is a meaningful value on both limit forms: it means UNMETERED, and it is a different thing
 * from an empty field. The control says which, because `row?.limit ?? null` turning a failed read
 * into "unlimited" is precisely the one-character defect the server-side union exists against.
 *
 * ── AND NOTHING HERE COMPUTES A VERDICT ────────────────────────────────────────────────────────
 *
 * "May another company be created" is `checkCapacity` on the server. This renders counts, sends
 * changes, and prints whatever answer comes back.
 */

import { useCallback, useState } from 'react';
import type { FormEvent } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { CapacityPanel } from '../components/CapacityPanel';
import { CataloguePicker, type PickerOption } from '../components/CataloguePicker';
import { ServerRead } from '../components/ServerRead';
import { SetupProgressPanel } from '../components/SetupProgressPanel';
import { FactList } from '../components/FactList';
import { SkeletonRows, StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { SubscriptionState, TenantLifecycleRefusal, TenantStatus } from '../api/contract';
import { SUBSCRIPTION_STATES, TENANT_STATUSES } from '../api/contract';
import { formatInstantHe, subscriptionStateHe } from '../api/adminVocabulary';
import { subscriptionStatesHe, tenantLifecycleRefusalText } from '../api/lifecycleText';
import { subscriptionItemRefusalText } from '../api/subscriptionItemText';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { catalogueStatusHe } from '../api/adminVocabulary';
import { callOperation } from '../api/operations';
import { listOf, useServerData } from '../api/useServerData';
import type { TenantAdminSummary } from '../api/contract';
import { routes } from '../routes';

export interface PlatformSubscriptionScreenProps {
  readonly tenantUid: string;
}

const TITLE_HE = 'מנוי ומכסות';

/**
 * The id of the ONE commercial-reason field, so a blocked action can send focus to it rather than
 * describing where it is. A constant because it is written in two places and a drifting pair would
 * leave the link pointing at nothing — silently, since `getElementById` of a missing id is `null`.
 */
const REASON_FIELD_ID = 'commercial-reason';

/** What was just changed, named in the words an operator used to ask for it. */
type Changed =
  | 'plan'
  | 'sku'
  | 'planComposition'
  | 'trial'
  | 'trialExtension'
  | 'companyLimit'
  | 'seatLimit'
  | 'state';

const CHANGED_HE: Readonly<Record<Changed, string>> = {
  plan: 'התכנית שויכה למנוי',
  sku: 'הפריט נוסף למנוי',
  planComposition: 'הרכב התכנית הוחל על המנוי',
  trial: 'תקופת הניסיון עודכנה',
  trialExtension: 'תקופת הניסיון הוארכה',
  companyLimit: 'מכסת החברות עודכנה',
  seatLimit: 'מכסת המשתמשים עודכנה',
  state: 'מצב המנוי שונה',
};

type Outcome =
  | {
      readonly kind: 'changed';
      readonly what: Changed;
      readonly overCapacityBy: number | null;
      /**
       * 08 §5.3. `YYYY-MM-DD` when the act was RECORDED for a later day rather than performed —
       * the mid-period downgrade, which takes effect the day after the period ends.
       *
       * `null` is "it happened now", which is every other write on this screen. The distinction is
       * the whole field: *"התכנית שויכה למנוי"* about a plan nobody is on yet is a sentence an
       * operator acts on.
       */
      readonly scheduledFrom: string | null;
      /** The recorded move replaced an earlier one that had not taken effect. */
      readonly supersededSchedule: boolean;
    }
  /*
   * A FAILURE NAMES THE ACT IT FAILED. It did not, and that is why every refusal on this screen
   * arrived as a tenant-lifecycle sentence: one renderer served seven different actions, so the
   * four PRICING refusals of `platform.subscription.sku.add` -- which this build had no Hebrew for
   * at all -- fell through to the generic banner the Owner saw on 2026-10-20.
   */
  | { readonly kind: 'failed'; readonly what: Changed; readonly failure: ActionFailure }
  /** What applying the plan's composition actually granted. Three lists, never a count. */
  | {
      readonly kind: 'planApplied';
      readonly included: readonly string[];
      readonly alreadyLive: readonly string[];
      readonly notActive: readonly { readonly skuCode: string; readonly status: string }[];
    };

function knownTenantStatuses(values: readonly string[] | undefined): readonly TenantStatus[] {
  const known: readonly string[] = TENANT_STATUSES;
  return (values ?? []).filter((v): v is TenantStatus => known.includes(v));
}

function knownSubscriptionStates(values: readonly string[] | undefined): readonly SubscriptionState[] {
  const known: readonly string[] = SUBSCRIPTION_STATES;
  return (values ?? []).filter((v): v is SubscriptionState => known.includes(v));
}

export function PlatformSubscriptionScreen({ tenantUid }: PlatformSubscriptionScreenProps): JSX.Element {
  const ctx = useWorkContext();

  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }

  return <PlatformSubscriptionBody tenantUid={tenantUid} />;
}

/**
 * THE BODY, AND WHY IT IS A SEPARATE COMPONENT.
 *
 * Every hook on this screen lives here, BELOW the surface guard, because `ctx.surface` is `null`
 * until `auth.whoami.read` answers (`shell/ContextProvider.tsx:181`) and every in-app navigation is
 * a full page load. Declared in one component with the guard above them, the early return would run
 * on the first (unresolved) render and the hooks on the second — React then sees two different hook
 * orders for one component and throws DURING RENDER, and the arrival is a blank page.
 *
 * It is a SPLIT and not a hoist. `useServerData` has no `enabled` option
 * (`api/useServerData.ts:73`), so lifting the reads above the guard would issue a PLATFORM read on
 * an unresolved or wrong-surface render — a denied request, and an audited write, for a screen the
 * caller is not on. Here nothing is read until the session has been stated as PLATFORM, and each
 * component's hook order is unconditional.
 */
function PlatformSubscriptionBody({ tenantUid }: PlatformSubscriptionScreenProps): JSX.Element {
  const [planCode, setPlanCode] = useState('');
  const [skuCode, setSkuCode] = useState('');
  // QUANTITY IS NOT OPTIONAL. `platform.subscription.sku.add` refuses a body whose `quantity` is not
  // a number, and this form sent `{skuCode, reason}` — so "add an item to the subscription" was a
  // control that could only ever produce a 400. The operation's declared body is what surfaced it.
  const [skuQuantity, setSkuQuantity] = useState('1');
  const [trialEndsAt, setTrialEndsAt] = useState('');
  const [companyLimit, setCompanyLimit] = useState('');
  const [seatLimit, setSeatLimit] = useState('');
  const [unmeteredCompanies, setUnmeteredCompanies] = useState(false);
  const [unmeteredSeats, setUnmeteredSeats] = useState(false);
  const [toState, setToState] = useState<SubscriptionState>('active');
  const [reason, setReason] = useState('');
  const [trialStartsAt, setTrialStartsAt] = useState('');
  /*
   * WHICH ACTION IS IN FLIGHT, not merely THAT one is. **Owner, 2026-10-23 §5.**
   *
   * This was a boolean, so a request started at the bottom of the page disabled all eight buttons
   * and told nobody which of them was working. The identity is what lets each group print its own
   * progress beside its own button — *"אין להשאיר משתמש לוחץ בלי להבין אם בוצעה בקשה"* — while
   * still serialising the writes, because every button is disabled while any one is sending.
   */
  const [sending, setSending] = useState<Changed | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  /*
   * ---- BOTH FACTS COME FROM THE SERVER --------------------------------------------------------
   *
   * THESE TWO LINES USED TO BE:
   *
   *     const tenant = (fixturesEnabled() ? fixtureTenants : []).find(...) ?? null;
   *     const subscription = fixturesEnabled() ? fixtureSubscription : null;
   *
   * In Production both collapse: `fixturesEnabled()` is false, so the customer was never found and
   * the subscription was always null. This screen carries ELEVEN write operations -- plan, SKUs,
   * trial, state, two limits -- every one of them pointed at the real `tenantUid` in the address.
   * A panel that cannot read the state it is about to change is worse than one that is missing.
   *
   * The identity comes from `platform.tenant.read`, which is where every other control-plane
   * screen resolves a customer. The subscription comes from `platform.subscription.read`, which
   * did not exist: the platform surface had eleven ways to WRITE a subscription and no way to read
   * one back, so nothing on this screen could ever have shown the result of its own actions.
   */
  const readTenants = useCallback(() => callOperation('platform.tenant.read', {}), []);
  const readSubscription = useCallback(
    () => callOperation('platform.subscription.read', { params: { tenant_uid: tenantUid } }),
    [tenantUid],
  );
  /*
   * AND THE CAPACITY, WHICH NOTHING READ.
   *
   * `setLimit` has existed since 0003 and no route returned the row it writes, so this screen said
   * "there is no server read" in place of the two numbers it is named after. `platform.capacity.read`
   * is that read. Its own `ServerRead` because a customer whose subscription loaded and whose
   * capacity did not is a real state, and one failure must not blank the other answer.
   */
  const readCapacity = useCallback(
    () => callOperation('platform.capacity.read', { params: { tenant_uid: tenantUid } }),
    [tenantUid],
  );
  const tenantsRead = useServerData(readTenants);
  const subscriptionRead = useServerData(readSubscription);
  const capacityRead = useServerData(readCapacity);

  /*
   * ---- AND WHAT THE PLATFORM ACTUALLY SELLS ---------------------------------------------------
   *
   * Both boxes below were free text. `billing.plan` and `billing.product_sku` have been seeded
   * since 0003 and read by nothing, so an operator recalled a code and a typo came back as a
   * refusal that named no alternative. These two reads are the list behind the boxes.
   *
   * NEITHER IS A DROPDOWN. 09 §3.4 keeps every selector in the shell; a module that owned one
   * would be a second place a context gets chosen. Plans are radio buttons -- there are a handful,
   * and the choice is visible without opening anything -- and SKUs are a `datalist` on the same
   * text input, which suggests without replacing what the operator may type.
   */
  const readPlans = useCallback(() => callOperation('platform.catalogue.plan.read', {}), []);
  const readSkus = useCallback(() => callOperation('platform.catalogue.sku.read', {}), []);
  const plansRead = useServerData(readPlans);
  const skusRead = useServerData(readSkus);

  const tenant =
    listOf<TenantAdminSummary, 'tenants'>(tenantsRead.result, 'tenants').find(
      (t) => t.tenantUid === tenantUid,
    ) ?? null;
  const subscription =
    subscriptionRead.result.state === 'loaded' ? subscriptionRead.result.data.subscription : null;
  const back = { labelHe: 'כרטיס הלקוח', href: routes.platformTenants };

  /*
   * FOUR STATES, RENDERED AS FOUR. Loading, refused/failed, no-such-customer, and a customer with
   * no subscription -- which is the one state further down, because it is a fact about a real
   * customer rather than a failure to find one.
   */
  if (tenantsRead.result.state === 'loading' || subscriptionRead.result.state === 'loading') {
    return (
      <AppShell titleHe={TITLE_HE} backToParent={back}>
        <SkeletonRows rows={5} />
      </AppShell>
    );
  }

  if (tenantsRead.result.state === 'failed') {
    return (
      <AppShell titleHe={TITLE_HE} backToParent={back}>
        <ActionFailureNotice failure={tenantsRead.result.failure} />
      </AppShell>
    );
  }

  if (tenant === null) {
    return (
      <AppShell titleHe={TITLE_HE} backToParent={back}>
        <StateBlock
          titleHe="הלקוח אינו נמצא"
          bodyHe="הקריאה לשרת הצליחה, ואין ברשימת הלקוחות לקוח בכתובת הזו."
          tone="unavailable"
          nextStep={
            <a className="btn btn--nav" href={routes.platformTenants}>
              חזרה לרשימת הלקוחות
            </a>
          }
        />
      </AppShell>
    );
  }

  if (subscriptionRead.result.state === 'failed') {
    // The customer EXISTS and their subscription could not be read. Two different sentences, and
    // this one must not be told as "this customer has no subscription".
    return (
      <AppShell titleHe={TITLE_HE} backToParent={back}>
        <ActionFailureNotice failure={subscriptionRead.result.failure} />
      </AppShell>
    );
  }

  /**
   * ONE OUTCOME HANDLER, SEVEN OPERATIONS.
   *
   * This used to take a PATH FRAGMENT and a bare `unknown` body, which is two holes at once: the
   * address was assembled from a literal no scanner could attribute to a route, and the body was
   * unchecked against the handler that reads it. Closing the second hole found the SKU defect below.
   *
   * It now takes the CALL, so every form names its operation as a literal and the reachability scan
   * sees seven distinct actions rather than one function called seven times.
   */
  async function send(
    what: Changed,
    call: () => Promise<{
      readonly overCapacityBy?: number;
      readonly scheduledFrom?: string;
      readonly supersededSchedule?: boolean;
    }>,
  ): Promise<void> {
    setSending(what);
    setOutcome(null);
    try {
      // The limit routes answer with `overCapacityBy` — how far under current usage the new limit
      // sits. It is reported and never treated as an error: rule 1, nothing existing is degraded.
      //
      // AND THE PLAN ROUTE MAY ANSWER `scheduledFrom`, which is 08 §5.3's mid-period downgrade:
      // recorded for the day after the period ends rather than applied now.
      const result = await call();
      setOutcome({
        kind: 'changed',
        what,
        overCapacityBy: result?.overCapacityBy ?? null,
        scheduledFrom: result?.scheduledFrom ?? null,
        supersededSchedule: result?.supersededSchedule === true,
      });
    } catch (err: unknown) {
      setOutcome({ kind: 'failed', what, failure: classifyActionError(err) });
    } finally {
      setSending(null);
    }
  }

  /**
   * AN ENTITLEMENT-LINE REFUSAL, in its own words. **The 2026-10-20 finding.**
   *
   * `platform.subscription.sku.add` is a sale and refuses what it cannot price; the plan-composition
   * route refuses a plan that includes nothing. Neither is a tenant-lifecycle transition, and
   * rendering them through `tenantLifecycleRefusalText` is what turned four addressable reasons --
   * publish a book, price the SKU, wait for the effective date, add a band -- into one sentence.
   *
   * THE FAULT REFERENCE IS SHOWN HERE and nowhere else on the screen: it belongs to a refusal an
   * operator may need to quote, and it is the id the audit row is keyed by.
   */
  function renderItemConflict(failure: Extract<ActionFailure, { kind: 'conflict' }>): JSX.Element {
    const text = subscriptionItemRefusalText(failure.body);
    return (
      <section className="denial" data-tone={text.tone === 'error' ? 'blocked' : 'warn'} role="alert">
        <h2 className="denial__title">{text.titleHe}</h2>
        <p className="denial__explanation">{text.explanationHe}</p>
        <p className="denial__explanation">{text.unchangedHe}</p>
        <p className="denial__next-step">{text.nextStepHe}</p>
        <p className="denial__explanation">{`אסמכתת תקלה: ${failure.requestUid}`}</p>
      </section>
    );
  }

  /**
   * APPLYING THE PLAN'S COMPOSITION, AND WHY IT IS NOT `send()`.
   *
   * `send()` ends with `setOutcome({kind: 'changed', …})`, which is right for the six writes that
   * have one outcome and wrong here: this action's answer is THREE LISTS, and routing it through
   * the shared helper overwrote them with "נשמר" — measured, in the dom test beside this screen.
   * The failure path is shared deliberately: a refusal here is rendered by the same mapper as a
   * sale's, because the operator does not experience them as different subjects.
   */
  async function applyComposition(): Promise<void> {
    setSending('planComposition');
    setOutcome(null);
    try {
      const result = await callOperation('platform.subscription.plan.apply', {
        params: { tenant_uid: tenantUid },
        // NO BODY, and `{}` is how this client says so for a POST: the route takes the plan from
        // the subscription and accepts nothing from the browser.
        body: {},
      });
      setOutcome({
        kind: 'planApplied',
        included: result.included,
        alreadyLive: result.alreadyLive,
        notActive: result.notActive,
      });
    } catch (err: unknown) {
      setOutcome({ kind: 'failed', what: 'planComposition', failure: classifyActionError(err) });
    } finally {
      setSending(null);
    }
  }

  /** A commercial conflict, in the customer's own current state and the states it may move from. */
  function renderCommercialConflict(failure: Extract<ActionFailure, { kind: 'conflict' }>): JSX.Element {
    const refusal: TenantLifecycleRefusal = {
      reason: failure.body.error,
      currentStatus:
        knownTenantStatuses(failure.body.currentStatus === undefined ? [] : [failure.body.currentStatus])[0] ??
        null,
      permittedFrom: knownTenantStatuses(failure.body.permittedFrom),
    };
    const text = tenantLifecycleRefusalText(refusal);
    const states = knownSubscriptionStates(failure.body.permittedStates);
    return (
      <section className="denial" data-tone="blocked" role="alert">
        <h2 className="denial__title">{text.titleHe}</h2>
        <p className="denial__explanation">{text.explanationHe}</p>
        <p className="denial__explanation">{text.unchangedHe}</p>
        {/* The closed list of states, in Hebrew. 04 §13.2.1 exposes it on refusal for exactly this. */}
        {states.length === 0 ? null : (
          <p className="denial__explanation">{`המצבים האפשריים: ${subscriptionStatesHe(states)}.`}</p>
        )}
        <p className="denial__next-step">{text.nextStepHe}</p>
      </section>
    );
  }

  /**
   * ── WHAT THIS ONE ACTION IS DOING, BESIDE THAT ONE ACTION. **Owner, 2026-10-23 §5.** ─────────
   *
   * *"הצג התקדמות ותוצאה היכן שהמשתמש מביט, גם בתחתית עמוד ארוך."*
   *
   * This screen had ONE outcome block for eight writes, rendered above every form. An operator who
   * pressed "קביעת המכסה" near the bottom of a page three screens tall wrote the answer somewhere
   * they could not see, which is indistinguishable from a button that does nothing — and it is the
   * reading the Owner reported. The outcome is keyed by the action that produced it, so each group
   * renders its own and no group renders another's.
   *
   * `role="status"` rather than `role="alert"` for the two non-failure states: a saved value is not
   * an alert, and an operator using a screen reader should hear it without being interrupted. The
   * refusal renderers below keep `role="alert"`, which is what a refusal is.
   */
  function feedbackFor(what: Changed): JSX.Element | null {
    if (sending === what) {
      return (
        <p className="action-state" data-state="sending" role="status">
          <span className="action-state__mark" aria-hidden="true" />
          נשלח לשרת…
        </p>
      );
    }
    if (outcome === null) return null;
    if (outcome.kind === 'changed' && outcome.what === what) {
      /*
       * ── A RECORDED MOVE AND AN APPLIED ONE DO NOT GET THE SAME SENTENCE. 08 §5.3 ─────────────
       *
       * A downgrade asked for mid-period is written down for the day after the period ends and the
       * customer stays on the plan they are paying for until then. `CHANGED_HE.plan` says *"התכנית
       * שויכה למנוי"*, which would be a claim about a change that has not happened — and the one
       * thing an operator needs from this screen is the DATE, so they can tell the customer.
       */
      if (outcome.scheduledFrom !== null) {
        return (
          <>
            <p className="action-state" data-state="done" role="status">
              <span className="action-state__mark" aria-hidden="true" />
              {`ההורדה נרשמה ותיכנס לתוקף ב־${outcome.scheduledFrom}`}
            </p>
            <p className="form__note">
              {[
                'התכנית הנוכחית נשארת בתוקף עד סוף התקופה שהלקוח משלם עליה, ולכן אין חיוב חלקי ואין זיכוי.',
                'מחזור החיוב היומי מבצע את המעבר באותו יום, ובודק שוב שהמכסות החדשות אינן נמוכות מהשימוש בפועל.',
                outcome.supersededSchedule
                  ? 'הרישום הזה מחליף הורדה מתוזמנת קודמת שטרם נכנסה לתוקף.'
                  : '',
              ]
                .filter((line) => line !== '')
                .join(' ')}
            </p>
          </>
        );
      }
      return (
        <>
          <p className="action-state" data-state="done" role="status">
            <span className="action-state__mark" aria-hidden="true" />
            {CHANGED_HE[what]}
          </p>
          <p className="form__note">
            {outcome.overCapacityBy === null || outcome.overCapacityBy === 0
              ? 'השינוי נרשם ביומן יחד עם הסיבה ועם מי שביצע אותו. המספרים במסך יתעדכנו בטעינה הבאה.'
              : `השינוי נרשם. הלקוח נמצא כעת ${String(outcome.overCapacityBy)} מעל המכסה שנקבעה — כל מה שקיים ממשיך לעבוד, ולא ניתן להוסיף חדשים.`}
          </p>
        </>
      );
    }
    if (outcome.kind === 'planApplied' && what === 'planComposition') {
      return (
        <>
          <p className="action-state" data-state="done" role="status">
            <span className="action-state__mark" aria-hidden="true" />
            {outcome.included.length > 0
              ? `הזכאות הוחלה: ${outcome.included.join(', ')}`
              : 'הרכב התכנית נבדק — לא היה מה להוסיף'}
          </p>
          <p className="form__note">
            {[
              outcome.included.length > 0
                ? `נוספו ${String(outcome.included.length)} שורות זכאות ללא מחיר, כפי שחבילה כוללת אותן.`
                : 'כל מה שהתכנית כוללת כבר היה במנוי, ולכן לא נכתבה שום שורה חדשה.',
              outcome.alreadyLive.length === 0
                ? ''
                : `היו כבר במנוי ולא נגענו בהם: ${outcome.alreadyLive.join(', ')}.`,
              outcome.notActive.length === 0
                ? ''
                : `כלולים בתכנית ולא הוענקו, כי מצבם בקטלוג אינו פעיל: ${outcome.notActive
                    // The catalogue status in Hebrew, not `planned` — the same correction the two
                    // catalogue grids needed. **Owner, 2026-10-23 §6.**
                    .map((row) => `${row.skuCode} (${catalogueStatusHe(row.status)})`)
                    .join(', ')}.`,
              'הזכאות נכנסת לתוקף בבקשה הבאה של הלקוח.',
            ]
              .filter((line) => line !== '')
              .join(' ')}
          </p>
        </>
      );
    }
    if (outcome.kind === 'failed' && outcome.what === what) {
      return (
        <ActionFailureNotice
          failure={outcome.failure}
          /*
           * THE FACTOR IS GIVEN HERE. **Owner section 3, 2026-10-27.**
           *
           * Without this handler `ActionFailureNotice` has no way to satisfy an MFA demand in
           * place and falls through to the arm that LINKS TO ANOTHER ADDRESS, which unmounts
           * this screen and loses whatever was half-entered. Every write this screen makes is
           * declared `per_action`, so the demand is a state it really reaches.
           *
           * NOTHING IS RE-SENT. `TenantUsersScreen` states the rule: *"A write that replays
           * itself after an authentication step is a write nobody pressed."* Clearing the
           * refusal puts the person back where they were, and they press again.
           */
          onStepUpSatisfied={() => setOutcome(null)}
          renderConflict={
            what === 'sku' || what === 'planComposition' ? renderItemConflict : renderCommercialConflict
          }
        />
      );
    }
    return null;
  }

  /**
   * WHY THIS BUTTON CANNOT BE PRESSED, SAID NEXT TO IT. **Owner, 2026-10-23 §5.**
   *
   * *"פעולה שאינה זמינה תציג סיבה סמוכה ודרך להשלים את החסר."* Five of the writes on this screen
   * are refused by the server without a substantive reason — `commercial.ts` checks
   * `reasonIsSubstantive` in `assignPlan`, `extendTrial`, both limit setters and
   * `changeSubscriptionState` — and the field holding it used to be the LAST element on the page.
   * So four of those buttons looked available, were pressed, and came back refused; the fifth was
   * disabled with no stated cause and its explanation three screens below.
   *
   * The link moves focus rather than only the scroll position: an operator who lands next to a
   * field still has to find it, and `:target` highlights nothing a keyboard is using.
   */
  function missingReason(): JSX.Element | null {
    if (reason.trim() !== '') return null;
    return (
      <div className="action-blocked">
        <p className="action-blocked__reason">
          הפעולה דורשת סיבה. השרת דוחה שינוי מסחרי בלי סיבה כתובה, והיא נרשמת ביומן יחד עם מי שביצע
          אותו.
        </p>
        <p>
          <a
            className="btn btn--quiet"
            href={`#${REASON_FIELD_ID}`}
            onClick={() => {
              document.getElementById(REASON_FIELD_ID)?.focus();
            }}
          >
            מעבר לשדה הסיבה
          </a>
        </p>
      </div>
    );
  }

  if (subscription === null) {
    /*
     * 08 §5.1: a customer with no subscription is a real state, and the operator's job here is to
     * open one.
     *
     * THE LINK USED TO POINT AT `/platform/tenants/new`, which starts a NEW customer at step 1 —
     * the one address that cannot help the customer this page is describing. Re-entering the same
     * handle there answers `slug_taken`, correctly, and the operator is back where they started.
     * It points at this customer's own provisioning address now.
     */
    return (
      <AppShell titleHe={TITLE_HE} backToParent={back}>
        <StateBlock
          titleHe="ללקוח אין עדיין מנוי"
          bodyHe="לא נרשם מנוי ללקוח הזה, ולכן אין לו מכסות ואין לו מודולים."
          tone="empty"
          nextStep={
            <a
              className="btn btn--nav"
              href={routes.platformTenantProvisioningResume(tenantUid)}
            >
              המשך הקמת הלקוח
            </a>
          }
        />
      </AppShell>
    );
  }

  /**
   * HOW MANY PRODUCTS THIS CUSTOMER'S PLAN INCLUDES, from the catalogue the screen already reads.
   *
   * `null` means the question could not be answered -- the catalogue read failed, or the plan the
   * subscription is on is not in it -- and the screen says that instead of showing `0`, which
   * would be a claim that the plan is empty. The figure comes from `skuCount` on the plan row;
   * nothing here recomputes a composition the server owns.
   */
  const includedCount: number | null = (() => {
    if (plansRead.result.state !== 'loaded') return null;
    const plan = plansRead.result.data.plans.find((row) => row.planCode === subscription.planCode);
    const count = plan === undefined ? null : plan.skuCount;
    // A SERVER THAT DID NOT SEND THE FIELD IS NOT A PLAN WITH ZERO PRODUCTS. The contract declares
    // `skuCount` required; a build talking to an older API would otherwise read `undefined` and
    // render it as a number, which is the class of defect `subscriptionState` already produced once.
    return typeof count === 'number' ? count : null;
  })();

  /*
   * WHAT THE ITEM PICKER OFFERS. Only sellable products — this form is a SALE — with the Hebrew
   * name as the only thing searched and the code as a muted secondary, which is the correction of
   * 2026-10-20: the control it replaces was a `datalist` whose VALUE was the code, so an operator
   * typed and matched against `TEST_COMPANY` and the Hebrew name was decoration.
   */
  const itemOptions: readonly PickerOption[] = (
    skusRead.result.state === 'loaded' ? skusRead.result.data.skus : []
  )
    .filter((sku) => sku.sellable)
    .map((sku) => ({ value: sku.skuCode, labelHe: sku.displayNameHe, detail: sku.skuCode }));

  const state = subscriptionStateHe(subscription.state);

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'כרטיס הלקוח', href: routes.platformTenant(tenant.tenantUid) }}
    >
      {/* THE SAME PANEL, with the modules this screen has actually read -- so the step the
          provisioning screen would land on and the step named here are the same fact. */}
      <SetupProgressPanel
        tenantUid={tenantUid}
        subscriptionState={subscription.state}
        licensedModules={subscription.licensedModules}
        seatCount={tenant.seatCount}
      />

      <FactList
        facts={[
          { labelHe: 'הלקוח', value: tenant.legalName },
          // The catalogue CODE, which is what the platform sells and what the plan-assignment
          // form below writes. A Hebrew display name is a tenant-facing rendering and is not
          // carried by this read.
          { labelHe: 'קוד התכנית', value: subscription.planCode },
          { labelHe: 'מצב המנוי', value: <StatusChip labelHe={state.labelHe} tone={state.tone} /> },
          {
            labelHe: 'תקופת המנוי',
            value: (
              <span dir="ltr">
                {formatInstantHe(subscription.periodStart)} — {formatInstantHe(subscription.periodEnd)}
              </span>
            ),
          },
          {
            labelHe: 'סיום תקופת ניסיון',
            value: formatInstantHe(subscription.trialEndsAt),
            absentHe: 'אין תקופת ניסיון',
          },
        ]}
      />

      {/*
        THE TWO NUMBERS THIS SCREEN IS NAMED AFTER.
        This block used to be a written refusal: the limit could be WRITTEN by the two forms below
        and read by nothing, so a number here would have been one nobody measured. It is measured
        now -- the limit from `billing.plan_limit` and the use counted live -- and rendered by the
        same `CapacityPanel` the office sees about itself, so the two surfaces cannot disagree
        about what "18 companies" means.
      */}
      <ServerRead result={capacityRead.result} whatHe="את המכסות" skeletonRows={2}>
        {(data) => <CapacityPanel capacity={data.capacity} captionHe="מכסות שנרכשו" />}
      </ServerRead>

      {/*
        Module CODES, never SKUs. 08 D12: the gate never sees a SKU, so a screen that listed them
        would be describing a commercial object as though it were an access one.
      */}
      <section className="tiles">
        {subscription.licensedModules.map((code) => (
          <article key={code} className="tile">
            {/*
              A CODE IS NOT A HEADING. **Owner, 2026-10-23 §6:** *"בממשק עברית הצג שמות עסקיים
              בעברית בלי לשכפל קוד אנגלי לצד השם; קוד טכני יכול להופיע בפרטים."*

              AND THE HEBREW NAME IS NOT AVAILABLE TO THIS SCREEN, which is stated rather than
              invented. The seeded Hebrew module names come from `platform.catalogue.reference.read`,
              gated on `platform.catalogue.sku.manage` — a capability an OPERATOR does not hold, and
              `routes/platform.ts` says in as many words that the vocabulary was deliberately kept
              out of the Operator's reads. Issuing it here would widen what an Operator reads to
              improve a label. So the heading is the Hebrew for what the row IS, and the code stays
              as the technical detail it is.
            */}
            <h2 className="tile__title">מודול שנרכש במנוי</h2>
            <p className="tile__lead" dir="ltr">
              {code}
            </p>
          </article>
        ))}
      </section>

      <div className="actions-row">
        <a className="btn" href={routes.platformTenantCommercial(tenant.tenantUid)}>
          מסחר, הנחות ואישורים
        </a>
      </div>

      {/*
        ── THE REASON EVERY COMMERCIAL WRITE NEEDS, BEFORE THE WRITES AND NOT AFTER THEM ──────────

        It was the LAST element on the page, below eight forms, and five of those forms are refused
        by the server without it (`services/commercial.ts` — `reasonIsSubstantive`). So the field
        that unlocks the screen sat where nobody reaches before acting. **Owner, 2026-10-23 §4:**
        *"שמור על שדה, הסבר ופעולה באותה קבוצה"* and *"גלילה חוזרת בין חלקים קשורים"*.

        It stays ONE field for all of them — that was never the defect, and a reason per form would
        be eight chances to record a different story about one decision.
      */}
      <form className="form" onSubmit={(e: FormEvent) => e.preventDefault()}>
        <h2 className="form__title">סיבת השינוי המסחרי</h2>
        <div className="form__group form__group--wide">
          <label className="field" htmlFor={REASON_FIELD_ID}>
            <span className="field__label">הסיבה, במילים</span>
          </label>
          <input
            id={REASON_FIELD_ID}
            className="field__input"
            name="commercial-reason"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
          />
          <span className="field__hint">
            הסיבה נרשמת ביומן עם כל פעולה במסך הזה.
          </span>
        </div>
      </form>

      {/* ── ENTITLEMENT: the plan, the modules, the trial and the two limits ────────────────── */}
      <form
        className="form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void send('plan', () =>
            callOperation('platform.subscription.plan.assign', {
              params: { tenant_uid: tenantUid },
              body: { planCode, reason },
            }),
          );
        }}
      >
        <h2 className="form__title">התכנית והמודולים</h2>

        {/*
          THE CATALOGUE, AS THE CHOICE ITSELF. A failed read leaves the text box below working:
          the write takes a code, and losing the list must not lose the ability to act.
        */}
        <ServerRead result={plansRead.result} whatHe="את קטלוג התכניות" skeletonRows={3}>
          {(data) => (
            <fieldset className="field" aria-label="תכניות בקטלוג">
              <legend className="field__label">בחירת תכנית</legend>
              {data.plans.map((plan) => (
                <label key={plan.planCode} className="choice">
                  <input
                    type="radio"
                    name="planChoice"
                    value={plan.planCode}
                    checked={planCode === plan.planCode}
                    /* A RETIRED PLAN IS SHOWN AND CANNOT BE CHOSEN. Hiding it would leave the
                       customer sitting on it looking at a code the catalogue denies exists. */
                    disabled={!plan.assignable}
                    onChange={() => setPlanCode(plan.planCode)}
                  />
                  <span>{plan.displayNameHe}</span>
                  <span dir="ltr" className="company-cell__id">
                    {plan.planCode}
                  </span>
                  {plan.assignable ? null : <span className="field__hint">אינה ניתנת לשיוך</span>}
                </label>
              ))}
            </fieldset>
          )}
        </ServerRead>

        {/* THE CODE, ITS EXPLANATION AND ITS ACTION IN ONE BOX — §4's *"שדה, הסבר ופעולה באותה
            קבוצה"*. The box is one grid item, so the column system may move it and can never put
            the button a column away from the field it submits. */}
        <div className="form__group">
          <label className="field">
            <span className="field__label">קוד התכנית</span>
            <input
              className="field__input"
              name="planCode"
              dir="ltr"
              value={planCode}
              onChange={(e) => setPlanCode(e.currentTarget.value)}
            />
          </label>
          <div className="actions-row">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={sending !== null || planCode === '' || reason.trim() === ''}
            >
              שיוך התכנית
            </button>
            {feedbackFor('plan')}
          </div>
          {missingReason()}
        </div>
      </form>

      {/* ── THE PLAN'S OWN COMPOSITION, APPLIED. **The 2026-10-20 blocker.** ─────────────────
          A package's modules are not a sale. Until this action existed, including a product in a
          plan granted the customer NOTHING -- the entitlement resolver reads the subscription's
          own lines -- and the only way to create a line was to sell one, which needs a price the
          package already covers. This is the included half, and it writes no price. */}
      {/*
        NOT A `.breakdown`. That class is an auto-fit GRID built for figures, so its four children —
        the title, two paragraphs and the action row — were laid out as four CELLS, and the button
        ended up in a different column from the sentence explaining it. Rendered at 1440px on
        2026-10-23 it sat at the far edge of the screen from its own text, which is §4's *"פעולות
        רחוקות מהשדות ומההסברים"* produced by a layout rule rather than by a missing one.
      */}
      <section className="form" aria-label="הזכאות שהתכנית כוללת">
        <h2 className="form__title">הזכאות שהתכנית כוללת</h2>
        <div className="form__group form__group--wide">
          <p className="form__note">
            {includedCount === null
              ? 'התכנית של המנוי אינה מופיעה בקטלוג שנקרא, ולכן לא ניתן לומר כאן כמה מוצרים היא כוללת.'
              : includedCount === 0
                ? 'התכנית הזאת אינה כוללת אף מוצר, ולכן אין זכאות להחיל. הכללת מוצרים בתכנית נעשית בקטלוג.'
                : `התכנית כוללת ${String(includedCount)} מוצרים. ההחלה מוסיפה למנוי רק את מה שחסר בו, ללא מחיר — ` +
                  'כפי שחבילה כוללת מוצר — ואינה משנה פריט שכבר נמכר ללקוח הזה.'}
          </p>
          <p className="form__note">
            אין בכך חיוב ואין בכך מכירה. מוצר שמצבו בקטלוג אינו פעיל לא יוענק, והוא ייאמר בשמו.
          </p>
          <div className="actions-row">
            <button
              type="button"
              className="btn btn--primary"
              disabled={sending !== null || includedCount === 0}
              onClick={() => void applyComposition()}
            >
              החלת הרכב התכנית על המנוי
            </button>
            {feedbackFor('planComposition')}
          </div>
          {/* WHY IT CANNOT BE PRESSED, where it is. An empty plan is a different cause from a missing
              reason, and this action needs no reason — the server takes the composition from the
              subscription and accepts nothing from the browser. */}
          {includedCount === 0 ? (
            <div className="action-blocked">
              <p className="action-blocked__reason">
                התכנית הזאת אינה כוללת אף מוצר, ולכן אין זכאות להחיל.
              </p>
              <p>
                <a className="btn btn--quiet" href={routes.platformCatalogue}>
                  הכללת מוצרים בתכנית, בקטלוג
                </a>
              </p>
            </div>
          ) : null}
        </div>
      </section>

      <form
        className="form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void send('sku', () =>
            callOperation('platform.subscription.sku.add', {
              params: { tenant_uid: tenantUid },
              body: { skuCode, quantity: Number(skuQuantity) },
            }),
          );
        }}
      >
        <h2 className="form__title">הוספת פריט למנוי</h2>
        <div className="form__group form__group--wide">
        <CataloguePicker
          name="skuCode"
          labelHe="הפריט"
          options={itemOptions}
          value={skuCode}
          onChange={setSkuCode}
          required
          emptyHe="אין מוצרים למכירה בקטלוג. מוצר נוצר ומסומן למכירה במסך הקטלוג."
          hintHe="הפריט נפתח למודולים שהוא כולל. המודולים הם מה שהמערכת בודקת, והפריט עצמו אינו מגיע לבדיקה."
        />
        <label className="field">
          <span className="field__label">כמות</span>
          <input
            className="field__input"
            name="skuQuantity"
            dir="ltr"
            inputMode="numeric"
            value={skuQuantity}
            onChange={(e) => setSkuQuantity(e.currentTarget.value)}
          />
        </label>
          <div className="actions-row">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={
                sending !== null ||
                skuCode === '' ||
                skuQuantity === '' ||
                Number.isNaN(Number(skuQuantity))
              }
            >
              הוספת הפריט
            </button>
            {feedbackFor('sku')}
          </div>
        </div>
      </form>

      <form
        className="form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void send('trial', () =>
            callOperation('platform.trial.set', {
              params: { tenant_uid: tenantUid },
              body: {
                /*
                 * BOTH DATES, OR NEITHER — AND THIS FORM USED TO SEND ONLY ONE.
                 * **Found by rendering the screen, 2026-10-23.**
                 *
                 * `routes/commercial.ts` reads `trialStartsAt` with `nullableStr`, which separates
                 * ABSENT from `null`: the key was not in the body at all, so the handler returned
                 * `INVALID_REQUEST` before the service was reached. "קביעת תקופת הניסיון" could
                 * not succeed on any input. `CK_subscription_trial` is the rule underneath —
                 * `commercial.ts` refuses `invalid_trial_window` unless both are set or both null
                 * — so the window is now entered as a window.
                 *
                 * `null` is a MEANINGFUL value here: two empty fields CLEAR the trial, and that is
                 * an action rather than the absence of one.
                 */
                trialStartsAt: trialStartsAt === '' ? null : trialStartsAt,
                trialEndsAt: trialEndsAt === '' ? null : trialEndsAt,
                reason,
              },
            }),
          );
        }}
      >
        <h2 className="form__title">תקופת ניסיון</h2>
        <div className="form__group form__group--wide">
          <h3 className="form__group-title">קביעת התקופה</h3>
          <label className="field">
            <span className="field__label">מועד תחילת תקופת הניסיון</span>
            <input
              className="field__input"
              name="trialStartsAt"
              type="date"
              dir="ltr"
              value={trialStartsAt}
              onChange={(e) => setTrialStartsAt(e.currentTarget.value)}
            />
          </label>
          <label className="field">
            <span className="field__label">מועד סיום תקופת הניסיון</span>
            <input
              className="field__input"
              name="trialEndsAt"
              type="date"
              dir="ltr"
              value={trialEndsAt}
              onChange={(e) => setTrialEndsAt(e.currentTarget.value)}
            />
          </label>
          <span className="field__hint">
            תקופה היא שני מועדים: יש למלא את שניהם.
          </span>
          <div className="actions-row">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={
                sending !== null || (trialStartsAt === '') !== (trialEndsAt === '')
              }
            >
              קביעת תקופת הניסיון
            </button>
            {feedbackFor('trial')}
          </div>
          {(trialStartsAt === '') !== (trialEndsAt === '') ? (
            <div className="action-blocked">
              <p className="action-blocked__reason">
                מולא מועד אחד בלבד. השרת דוחה חצי תקופה — יש למלא את שני המועדים, או לרוקן את
                שניהם כדי לבטל את תקופת הניסיון.
              </p>
            </div>
          ) : null}
        </div>

        <div className="form__group">
          <h3 className="form__group-title">הארכת תקופה קיימת</h3>
          <p className="form__note">
            הארכה היא פעולה נפרדת מקביעה, כדי שביומן יירשם מה בדיוק נעשה: הארכה של תקופה קיימת אינה
            פתיחה של תקופה חדשה. היא נשלחת עם מועד הסיום שמילאת למעלה.
          </p>
          <div className="actions-row">
            <button
              type="button"
              className="btn"
              disabled={sending !== null || trialEndsAt === '' || reason.trim() === ''}
              onClick={() =>
                void send('trialExtension', () =>
                  callOperation('platform.trial.extend', {
                    params: { tenant_uid: tenantUid },
                    body: { trialEndsAt, reason },
                  }),
                )
              }
            >
              הארכת תקופת הניסיון
            </button>
            {feedbackFor('trialExtension')}
          </div>
          {trialEndsAt === '' ? (
            <div className="action-blocked">
              <p className="action-blocked__reason">
                אין מועד סיום למלא בבקשה. יש למלא את מועד הסיום בקבוצה שלמעלה.
              </p>
            </div>
          ) : (
            missingReason()
          )}
        </div>
      </form>

      <form
        className="form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void send('companyLimit', () =>
            callOperation('platform.limit.company.set', {
              params: { tenant_uid: tenantUid },
              body: { limit: unmeteredCompanies ? null : Number(companyLimit), reason },
            }),
          );
        }}
      >
        <h2 className="form__title">מכסת החברות</h2>
        <div className="form__group form__group--wide">
          <label className="field">
            <span className="field__label">מספר החברות שנרכשו</span>
            <input
              className="field__input"
              name="companyLimit"
              dir="ltr"
              inputMode="numeric"
              value={companyLimit}
              disabled={unmeteredCompanies}
              onChange={(e) => setCompanyLimit(e.currentTarget.value)}
            />
          </label>
          <label className="field field--inline">
            <input
              type="checkbox"
              name="unmeteredCompanies"
              checked={unmeteredCompanies}
              onChange={(e) => setUnmeteredCompanies(e.currentTarget.checked)}
            />
            <span className="field__label">ללא הגבלת מספר חברות</span>
          </label>
          <p className="form__note">אפשר לקבוע מכסה נמוכה ממה שכבר בשימוש. במקרה כזה כל החברות הקיימות ממשיכות לעבוד במלואן, ורק הקמה של חברה נוספת נחסמת. שום דבר אינו נמחק.</p>
          <div className="actions-row">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={sending !== null || reason.trim() === ''}
            >
              קביעת המכסה
            </button>
            {feedbackFor('companyLimit')}
          </div>
          {missingReason()}
        </div>
      </form>

      <form
        className="form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void send('seatLimit', () =>
            callOperation('platform.limit.seat.set', {
              params: { tenant_uid: tenantUid },
              body: { limit: unmeteredSeats ? null : Number(seatLimit), reason },
            }),
          );
        }}
      >
        <h2 className="form__title">מכסת המשתמשים</h2>
        <div className="form__group form__group--wide">
          <label className="field">
            <span className="field__label">מספר המושבים שנרכשו</span>
            <input
              className="field__input"
              name="seatLimit"
              dir="ltr"
              inputMode="numeric"
              value={seatLimit}
              disabled={unmeteredSeats}
              onChange={(e) => setSeatLimit(e.currentTarget.value)}
            />
          </label>
          <label className="field field--inline">
            <input
              type="checkbox"
              name="unmeteredSeats"
              checked={unmeteredSeats}
              onChange={(e) => setUnmeteredSeats(e.currentTarget.checked)}
            />
            <span className="field__label">ללא הגבלת מספר משתמשים</span>
          </label>
          <p className="form__note">אותו כלל חל כאן: מכסה נמוכה מהשימוש הקיים אינה משביתה אף משתמש, והיא חוסמת הזמנה של משתמש נוסף בלבד.</p>
          <div className="actions-row">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={sending !== null || reason.trim() === ''}
            >
              קביעת המכסה
            </button>
            {feedbackFor('seatLimit')}
          </div>
          {missingReason()}
        </div>
      </form>

      <form
        className="form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void send('state', () =>
            callOperation('platform.subscription.state.change', {
              params: { tenant_uid: tenantUid },
              body: { toState, reason },
            }),
          );
        }}
      >
        <h2 className="form__title">שינוי מצב המנוי</h2>
        <div className="form__group form__group--wide">
          <fieldset className="field field--choice">
            <legend className="field__label">המצב החדש</legend>
            {SUBSCRIPTION_STATES.map((candidate) => (
              <label key={candidate} className="choice">
                <input
                  type="radio"
                  name="toState"
                  value={candidate}
                  checked={toState === candidate}
                  onChange={() => setToState(candidate)}
                />
                <span>{subscriptionStateHe(candidate).labelHe}</span>
              </label>
            ))}
          </fieldset>
          <div className="actions-row">
            <button
              type="submit"
              className="btn btn--irreversible"
              disabled={sending !== null || reason.trim() === ''}
            >
              שינוי מצב המנוי
            </button>
            {feedbackFor('state')}
          </div>
          {missingReason()}
        </div>
      </form>

    </AppShell>
  );
}
